/**
 * SAISEN — Cart drawer reservation countdown (text + progress bar).
 *
 * The theme's own native <countdown-timer> element was found to restart
 * from its full duration every time the cart drawer's content re-renders
 * (which can happen more often than just "opening the drawer"), so it never
 * actually counts down continuously across a real shopping session — the
 * exact "resets artificially" problem this feature must avoid.
 *
 * This script replaces that display with a self-contained countdown that
 * persists its true end time in localStorage:
 * - A NEW 15-minute window is only ever created when the real Shopify cart
 *   (fetched from /cart.js, never guessed from the DOM) is non-empty AND no
 *   window is currently stored. This happens on script init (cart already
 *   had items when the page loaded) and right after a successful cart
 *   mutation (/cart/add.js, /change.js, /update.js, /clear.js) — exactly
 *   mirroring the pattern assets/saisen-gift-lid-sync.js already uses to
 *   react to cart changes from any entry point.
 * - While a window is stored, every render (drawer reopened, page
 *   navigated, cart re-rendered) reads that SAME stored end time and
 *   computes the real remaining time from it — the countdown only ever
 *   moves forward in real time, never resets on its own.
 * - When the stored end time is reached, the display simply freezes at 0:00
 *   with an empty bar. The countdown is purely visual: expiration NEVER
 *   touches the cart (no product, quantity, free lid or discount is changed,
 *   no request is sent, no page reload). The expired end time stays stored,
 *   so the countdown does not restart on its own while the cart still has
 *   items. A fresh 15-minute window is only created for a NEW cart, i.e.
 *   after the cart has become empty (customer emptied it, or order placed).
 *
 * Text and bar are always driven from the exact same remainingMs value, so
 * they can never disagree. The bar itself is updated on every animation
 * frame (not just once per second) via transform: scaleX(), so it depletes
 * as a smooth, continuous motion from right to left (fixed left edge,
 * transform-origin: left) instead of a barely-perceptible per-second jump.
 */
(function () {
  var STORAGE_KEY = 'saisen_cart_timer_end';
  var CART_MUTATION_RE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;

  var nativeFetch = window.fetch.bind(window);
  var lastRenderedSecond = {};
  var knownDuration = null; // duree (s) trouvee sur le dernier wrapper rencontre

  function formatTime(totalSeconds) {
    var minutes = Math.floor(totalSeconds / 60);
    var seconds = totalSeconds % 60;
    return minutes + ':' + (seconds < 10 ? '0' : '') + seconds;
  }

  function getStoredEnd() {
    try {
      var stored = localStorage.getItem(STORAGE_KEY);
      var end = stored ? parseInt(stored, 10) : NaN;
      return !end || isNaN(end) ? null : end;
    } catch (e) {
      return null;
    }
  }

  function clearStoredEnd() {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (e) {}
    cachedAt = 0;
  }

  // Perf : la fin de la fenetre est relue dans localStorage au plus une fois
  // par seconde (et immediatement apres tout changement fait par ce script),
  // au lieu d'une lecture a chaque image.
  var cachedEnd = null;
  var cachedAt = 0;
  function currentEnd() {
    var now = Date.now();
    if (now - cachedAt >= 1000) {
      cachedEnd = getStoredEnd();
      cachedAt = now;
    }
    return cachedEnd;
  }

  function startNewWindow(durationSeconds) {
    var end = Date.now() + durationSeconds * 1000;
    try {
      localStorage.setItem(STORAGE_KEY, String(end));
    } catch (e) {}
    cachedAt = 0;
    kick();
  }

  // N'est appelee que suite a une mutation reelle du panier (ou au chargement
  // initial) : verifie l'etat REEL du panier Shopify avant de demarrer quoi
  // que ce soit. Ne demarre jamais un timer juste parce que le bloc HTML du
  // minuteur est present dans le DOM (il l'est toujours, meme panier vide).
  function checkCartAndMaybeStart() {
    nativeFetch('/cart.js')
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        var storedEnd = getStoredEnd();
        if (cart && cart.item_count > 0 && storedEnd === null) {
          startNewWindow(knownDuration || 900);
        } else if (cart && cart.item_count === 0 && storedEnd !== null && storedEnd <= Date.now()) {
          // Panier vide ET fenetre expiree : on efface l'ancienne fenetre pour
          // qu'un futur NOUVEAU panier demarre son propre compte a rebours.
          clearStoredEnd();
        }
      })
      .catch(function () {});
  }

  // Expiration : affichage fige a 0:00, barre vide. Rien d'autre. Le panier
  // du client n'est jamais modifie par le compte a rebours.
  function showExpired(wrapper) {
    var valueEl = wrapper.querySelector('.saisen-cart-timer__value');
    if (valueEl && valueEl.textContent !== '0:00') valueEl.textContent = '0:00';
    var fill = wrapper.querySelector('.saisen-timer-bar__fill');
    if (fill && fill.style.transform !== 'scaleX(0)') fill.style.transform = 'scaleX(0)';
    lastRenderedSecond[wrapper.id] = 0;
  }

  // Le minuteur est-il a l'ecran ? Dans le tiroir : seulement tiroir ouvert.
  // Ailleurs (page panier) : toujours.
  function isOnScreen(wrapper) {
    var drawer = wrapper.closest('cart-drawer');
    return drawer ? drawer.classList.contains('active') : true;
  }

  var nextFrame = null; // { type: 'raf' | 'timeout', id }

  function schedule(smooth) {
    if (smooth) nextFrame = { type: 'raf', id: requestAnimationFrame(frame) };
    else nextFrame = { type: 'timeout', id: setTimeout(frame, 1000) };
  }

  // Relance immediate (ouverture du tiroir, nouvelle fenetre, retour sur l'onglet).
  function kick() {
    if (!nextFrame) return; // boucle pas encore demarree
    if (nextFrame.type === 'raf') cancelAnimationFrame(nextFrame.id);
    else clearTimeout(nextFrame.id);
    frame();
  }

  // Perf : l'animation fluide (une mise a jour par image) ne tourne que si un
  // compte a rebours est actif ET visible. Sinon une verification par seconde
  // suffit : le texte et la barre restent exactement synchronises sur la
  // meme heure de fin, comme avant.
  function frame() {
    var smooth = false;
    var end = currentEnd();
    var wrappers = document.querySelectorAll('.saisen-cart-timer[data-duration]');
    wrappers.forEach(function (wrapper) {
      var duration = parseInt(wrapper.dataset.duration, 10);
      if (!duration) return;
      knownDuration = duration;

      if (end === null) return; // aucun cycle actif : ne rien afficher/demarrer ici

      var remainingMs = end - Date.now();

      if (remainingMs <= 0) {
        showExpired(wrapper);
        return;
      }

      var remainingSeconds = Math.ceil(remainingMs / 1000);
      var valueEl = wrapper.querySelector('.saisen-cart-timer__value');
      if (valueEl && (lastRenderedSecond[wrapper.id] !== remainingSeconds || valueEl.textContent === '--:--')) {
        valueEl.textContent = formatTime(remainingSeconds);
        lastRenderedSecond[wrapper.id] = remainingSeconds;
      }

      var fill = wrapper.querySelector('.saisen-timer-bar__fill');
      if (fill) {
        var progress = Math.max(0, Math.min(1, remainingMs / (duration * 1000)));
        fill.style.transform = 'scaleX(' + progress + ')';
      }

      if (isOnScreen(wrapper)) smooth = true;
    });

    schedule(smooth && !document.hidden);
  }

  function patchFetch() {
    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var promise = nativeFetch.apply(window, arguments);
      if (CART_MUTATION_RE.test(url)) {
        promise.then(function () {
          checkCartAndMaybeStart();
        });
      }
      return promise;
    };
  }

  function start() {
    var initialWrapper = document.querySelector('.saisen-cart-timer[data-duration]');
    if (initialWrapper) knownDuration = parseInt(initialWrapper.dataset.duration, 10) || null;

    patchFetch();
    checkCartAndMaybeStart(); // couvre le cas "panier deja non vide au chargement de la page"
    schedule(true);

    // Reprise immediate de l'animation des que le minuteur redevient visible.
    var drawerEl = document.querySelector('cart-drawer');
    if (drawerEl && window.MutationObserver) {
      new MutationObserver(kick).observe(drawerEl, { attributes: true, attributeFilter: ['class'] });
    }
    document.addEventListener('visibilitychange', function () {
      if (!document.hidden) kick();
    });
    window.addEventListener('storage', function (event) {
      if (event.key === STORAGE_KEY) {
        cachedAt = 0;
        kick();
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
